import { useEffect, useState } from 'react';
import { toast } from '../../../ui/toast';
import {
  enrollBegin,
  enrollConfirm,
  mfaDisable,
  mfaStatus,
  regenerateRecoveryCodes,
} from '../../../utils/api_service/mfa_api';
import type { IMfaSetup, IMfaStatus } from '../interfaces';

export interface IUseTwoFactorSettings {
  loading: boolean;
  status: IMfaStatus | null;
  setup: IMfaSetup | null;
  code: string;
  setCode: (value: string) => void;
  recoveryCodes: string[];
  manageCode: string;
  setManageCode: (value: string) => void;
  busy: boolean;
  startSetup: () => Promise<void>;
  confirmSetup: () => Promise<void>;
  cancelSetup: () => void;
  disable: () => Promise<void>;
  regenerate: () => Promise<void>;
}

export const useTwoFactorSettings = (): IUseTwoFactorSettings => {
  const [loading, setLoading] = useState<boolean>(true);
  const [status, setStatus] = useState<IMfaStatus | null>(null);
  const [setup, setSetup] = useState<IMfaSetup | null>(null);
  const [code, setCode] = useState<string>('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [manageCode, setManageCode] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);

  const refresh = async (): Promise<void> => {
    try {
      const response = await mfaStatus();
      const data = await response.json();
      setStatus(data);
    } catch (error) {
      console.error('Failed to load MFA status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const startSetup = async (): Promise<void> => {
    setRecoveryCodes([]);
    const res = await enrollBegin({});
    const data = await res.json();
    if (!res.ok || !data.success) {
      toast.error('Could not start setup.', { position: 'bottom-center' });
      return;
    }
    setSetup({ secret: data.secret, otpauthUri: data.otpauthUri, enrollmentId: data.enrollmentId });
  };

  const confirmSetup = async (): Promise<void> => {
    if (!setup) return;
    setBusy(true);
    try {
      const res = await enrollConfirm({ enrollmentId: setup.enrollmentId, code });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast.error('That code was not accepted.', { position: 'bottom-center' });
        return;
      }
      setRecoveryCodes(data.recoveryCodes || []);
      setSetup(null);
      setCode('');
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const cancelSetup = (): void => {
    setSetup(null);
    setCode('');
  };

  const disable = async (): Promise<void> => {
    setBusy(true);
    try {
      const res = await mfaDisable(manageCode);
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast.error(
          data.error === 'mfa_required_by_policy'
            ? 'Your organization requires two-factor authentication, so it cannot be turned off.'
            : 'That code was not accepted.',
          { position: 'bottom-center' },
        );
        return;
      }
      setManageCode('');
      setRecoveryCodes([]);
      toast.success('Two-factor authentication disabled.', { position: 'bottom-center' });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async (): Promise<void> => {
    setBusy(true);
    try {
      const res = await regenerateRecoveryCodes(manageCode);
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast.error('That code was not accepted.', { position: 'bottom-center' });
        return;
      }
      setRecoveryCodes(data.recoveryCodes || []);
      setManageCode('');
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  return {
    loading,
    status,
    setup,
    code,
    setCode,
    recoveryCodes,
    manageCode,
    setManageCode,
    busy,
    startSetup,
    confirmSetup,
    cancelSetup,
    disable,
    regenerate,
  };
};
